import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { Radio, Clock, ChevronRight, RefreshCw, Trophy } from "lucide-react";

const POLL_MS = 4000;

const LEVEL_LABEL = {
  l0: "Round 1", l1: "Semi Final", l2: "Final", l3: "3rd Place",
  L0: "Round 1",  L1: "Semi Final",  L2: "Final",  L3: "3rd Place",
};

function Avatar({ avatar, name, size = 56 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className="rounded-xl overflow-hidden flex-shrink-0 border border-white/10"
      style={{ width: size, height: size * 1.33, background: "rgba(255,255,255,0.06)" }}
    >
      {avatar?.img ? (
        <img
          src={avatar.img}
          alt={name}
          className="w-full h-full object-cover object-top"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center font-display text-xl text-white/30">
          {initials}
        </div>
      )}
    </div>
  );
}

function PlayerVs({ p1, p2, score1, score2, isLive }) {
  return (
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {/* P1 */}
      <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
        <Avatar avatar={p1?.avatar} name={p1?.name} size={52} />
        <span className="font-display text-xs uppercase text-white leading-tight text-center truncate w-full max-w-[80px]">
          {p1?.name || "Player 1"}
        </span>
        {isLive && score1 !== undefined && (
          <span className="font-mono-kl text-lg font-bold text-[color:var(--kl-red)]">{score1}</span>
        )}
      </div>

      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <span className="font-display text-xl text-white/30">VS</span>
      </div>

      {/* P2 */}
      <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
        <Avatar avatar={p2?.avatar} name={p2?.name} size={52} />
        <span className="font-display text-xs uppercase text-white leading-tight text-center truncate w-full max-w-[80px]">
          {p2?.name || "Player 2"}
        </span>
        {isLive && score2 !== undefined && (
          <span className="font-mono-kl text-lg font-bold text-[color:var(--kl-red)]">{score2}</span>
        )}
      </div>
    </div>
  );
}

function LiveMatchCard({ match }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col gap-3 p-4"
      style={{
        background: "rgba(230,57,70,0.08)",
        border: "1.5px solid rgba(230,57,70,0.55)",
        boxShadow: "0 0 32px rgba(230,57,70,0.25), 0 0 6px rgba(230,57,70,0.18)",
      }}
    >
      {/* Animated red glow pulse ring */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse"
        style={{ boxShadow: "0 0 40px rgba(230,57,70,0.18) inset" }}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex items-center gap-1.5 font-mono-kl text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
            style={{ background: "rgba(230,57,70,0.85)", color: "#fff" }}
          >
            <Radio className="w-2.5 h-2.5" />
            LIVE
          </span>
          <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">
            {LEVEL_LABEL[match.level] || match.level}
          </span>
        </div>
        <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30">
          Match #{match.match_num ?? "—"}
        </span>
      </div>

      {/* Tournament name */}
      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] truncate">
        {match.tournament_name}
      </div>

      {/* Players */}
      <PlayerVs
        p1={match.player1} p2={match.player2}
        score1={match.player1?.total} score2={match.player2?.total}
        isLive
      />
    </div>
  );
}

function UpcomingMatchCard({ match }) {
  const statusLabel = match.status === "admin_started" ? "Starting Soon" : "Up Next";
  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col gap-3 p-4"
      style={{
        background: "rgba(255,195,0,0.05)",
        border: "1.5px solid rgba(255,195,0,0.28)",
        boxShadow: "0 0 18px rgba(255,195,0,0.10)",
      }}
    >
      {/* Subtle shimmer animation */}
      <div
        className="absolute inset-x-0 top-0 h-[2px] rounded-t-2xl animate-pulse"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,195,0,0.7), transparent)" }}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex items-center gap-1.5 font-mono-kl text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full"
            style={{ background: "rgba(255,195,0,0.18)", color: "#ffc300" }}
          >
            <Clock className="w-2.5 h-2.5" />
            {statusLabel}
          </span>
          <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40">
            {LEVEL_LABEL[match.level] || match.level}
          </span>
        </div>
        <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30">
          Match #{match.match_num ?? "—"}
        </span>
      </div>

      {/* Tournament name */}
      <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-yellow)] truncate">
        {match.tournament_name}
      </div>

      {/* Players */}
      <PlayerVs p1={match.player1} p2={match.player2} />
    </div>
  );
}

export default function LiveTournament() {
  const navigate = useNavigate();
  const [data, setData] = useState({ live: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.publicLiveMatches();
      setData(r);
      setLastUpdated(new Date());
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  const liveCount = data.live?.length ?? 0;
  const upcomingCount = data.upcoming?.length ?? 0;
  const total = liveCount + upcomingCount;

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* Background glow */}
      <div
        className="absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(230,57,70,0.55), transparent 65%)" }}
      />

      <div className="relative z-10 w-full px-6 md:px-12 py-10">

        {/* ── Top bar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <button
            onClick={() => navigate("/")}
            className="font-mono-kl text-xs uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-2"
          >
            ← Home
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/leaderboard")}
              className="font-mono-kl text-xs uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-1.5"
            >
              <Trophy className="w-3.5 h-3.5" />
              Leaderboard
              <ChevronRight className="w-3 h-3" />
            </button>
            {lastUpdated && (
              <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/25">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button onClick={load} className="text-white/40 hover:text-white transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* ── Hero heading ── */}
        <div className="font-mono-kl text-xs uppercase tracking-[0.35em] text-[color:var(--kl-red)] flex items-center gap-2 mb-2">
          <Radio className="w-3.5 h-3.5 animate-pulse" />
          Live Arena
        </div>
        <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.88] tracking-tight mb-2">
          What's <span className="text-[color:var(--kl-red)]">happening</span><br />right now.
        </h1>
        <p className="text-white/45 text-sm md:text-base mb-10 max-w-xl">
          Live battles in red · Upcoming matches in yellow · Refreshes every 4 seconds
        </p>

        {/* ── Stats pills ── */}
        <div className="flex flex-wrap gap-3 mb-10">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/40 text-xs font-mono-kl uppercase tracking-widest"
            style={{ background: "rgba(230,57,70,0.10)", color: "#e63946" }}>
            <Radio className="w-3 h-3 animate-pulse" /> {liveCount} Live
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-400/30 text-xs font-mono-kl uppercase tracking-widest"
            style={{ background: "rgba(255,195,0,0.08)", color: "#ffc300" }}>
            <Clock className="w-3 h-3" /> {upcomingCount} Upcoming
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-white/40 py-16">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-mono-kl text-xs uppercase tracking-widest">Loading matches…</span>
          </div>
        )}

        {!loading && total === 0 && (
          <div className="rounded-2xl border border-white/8 p-14 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
            <Radio className="w-10 h-10 text-white/15 mx-auto mb-4" />
            <div className="font-display text-3xl uppercase text-white/50">No matches right now</div>
            <p className="text-white/30 text-sm mt-2">Check back soon — matches will appear here as they start.</p>
          </div>
        )}

        {/* ── Live Matches ── */}
        {!loading && liveCount > 0 && (
          <section className="mb-12">
            <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-[color:var(--kl-red)] flex items-center gap-2 mb-4">
              <Radio className="w-3 h-3 animate-pulse" />
              Live Matches ({liveCount})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {data.live.map((m) => (
                <LiveMatchCard key={m.room_id} match={m} />
              ))}
            </div>
          </section>
        )}

        {/* ── Upcoming Matches ── */}
        {!loading && upcomingCount > 0 && (
          <section>
            <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-[color:var(--kl-yellow)] flex items-center gap-2 mb-4">
              <Clock className="w-3 h-3" />
              Upcoming Matches ({upcomingCount})
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {data.upcoming.map((m, i) => (
                <UpcomingMatchCard key={`${m.tournament_id}_${m.level}_${m.match_num}_${i}`} match={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
